import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { repositorioFazenda } from '../../sync/repositories/fazendas';
import { repositorioLote, repositorioPasto } from '../../sync/repositories/pecuaria';
import { extrairMensagensErro } from '../../utils/errorUtils';

const FASES = [
  { value: 'CR', label: 'Cria (0-8 meses)' },
  { value: 'RC', label: 'Recria (8-15 meses)' },
  { value: 'EG', label: 'Engorda (15+ meses)' },
];

const ESTADO_INICIAL = {
  nome: '', fazenda: '', pasto: '', data_entrada: '', peso_objetivo: '', gmd_previsto: '', fase: 'CR',
};

/**
 * Modal de cadastro rápido de Lote. Busca fazendas sozinho ao abrir, e
 * pastos em cascata sempre que a fazenda selecionada mudar (mesma regra
 * da tela completa: só pastos da fazenda escolhida).
 */
export default function ModalCadastroLote({ isOpen, onClose, onSuccess, fazendaSelecionadaId = null }) {
  const [dados, setDados] = useState(ESTADO_INICIAL);
  const [fazendas, setFazendas] = useState([]);
  const [pastos, setPastos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setDados(ESTADO_INICIAL);
      setPastos([]);
      setErros([]);
      setSalvando(false);
      return;
    }
    repositorioFazenda.listar()
      .then((resp) => setFazendas(Array.isArray(resp.data) ? resp.data : (resp.data.results || [])))
      .catch(() => setFazendas([]));
    setDados((prev) => ({ ...prev, fazenda: fazendaSelecionadaId || '' }));
  }, [isOpen, fazendaSelecionadaId]);

  useEffect(() => {
    if (!dados.fazenda) { setPastos([]); return; }
    repositorioPasto.listar(`?fazenda=${dados.fazenda}`)
      .then((resp) => setPastos(Array.isArray(resp.data) ? resp.data : (resp.data.results || [])))
      .catch(() => setPastos([]));
  }, [dados.fazenda]);

  const handleChange = (campo) => (e) => {
    const valor = e.target.value;
    setDados((prev) => ({ ...prev, [campo]: valor, ...(campo === 'fazenda' ? { pasto: '' } : {}) }));
  };

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!dados.nome.trim()) { setErros(['O nome do lote é obrigatório.']); return; }
    if (!dados.fazenda) { setErros(['Selecione a fazenda.']); return; }
    if (!dados.pasto) { setErros(['Selecione o pasto inicial.']); return; }
    if (!dados.data_entrada) { setErros(['Informe a data de entrada.']); return; }
    if (!dados.peso_objetivo) { setErros(['Informe o peso objetivo.']); return; }
    if (!dados.gmd_previsto) { setErros(['Informe o GMD previsto.']); return; }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioLote.criar({
        nome: dados.nome.trim(),
        fazenda: dados.fazenda,
        pasto: dados.pasto,
        data_entrada: dados.data_entrada,
        peso_objetivo: Number(dados.peso_objetivo),
        gmd_previsto: Number(dados.gmd_previsto),
        fase: dados.fase,
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Lote" size="lg">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Lote *</label>
            <input
              type="text"
              value={dados.nome}
              onChange={handleChange('nome')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              placeholder="Ex: Lote 01/2026"
              autoFocus
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fase *</label>
            <select
              value={dados.fase}
              onChange={handleChange('fase')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              {FASES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
            <select
              value={dados.fazenda}
              onChange={handleChange('fazenda')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {fazendas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pasto Inicial *</label>
            <select
              value={dados.pasto}
              onChange={handleChange('pasto')}
              disabled={salvando || !dados.fazenda}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">{dados.fazenda ? 'Selecione...' : 'Selecione a fazenda primeiro'}</option>
              {pastos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data de Entrada *</label>
            <input
              type="date"
              value={dados.data_entrada}
              onChange={handleChange('data_entrada')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Peso Objetivo (kg) *</label>
            <input
              type="number" step="0.01" min="1"
              value={dados.peso_objetivo}
              onChange={handleChange('peso_objetivo')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GMD Previsto (kg/dia) *</label>
            <input
              type="number" step="0.01" min="0.1"
              value={dados.gmd_previsto}
              onChange={handleChange('gmd_previsto')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
        </div>
        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
